import {
  Box,
  Stack,
  Theme,
  colors,
  Popover,
  hexToRgb,
  useTheme,
  ButtonBase,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import NextImage from "next/image";
import { useRouter } from "next/router";
import { CloseRounded } from "@mui/icons-material";

import { Divider } from "@/shared/base/components/Divider/Divider";

import { pluralize } from "@/global/utils/utils";
import {
  eventsState,
  selectEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppDispatch, useAppStore } from "@/global/store";
import { purchaseSeatsTabUIDs } from "@/global/config/constants";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";

import type { EventType } from "@/global/store/data/types";
import { useSelectActiveEvent } from "../ChooseSeats/useSelectActiveEvent";
import { isPackBonusEvent } from "../Packs/packs.utils";
import { selectSelectedPack } from "@/global/store/data/purchaseSeats/packs";

export function BottomBarEvents(props: { maxVisibleIconCount: number }) {
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { languages, purchaseSeatConfigs } = useTeamConfig();
  const styles = useEventIconWrapperStyles(theme);
  const { activeEvent } = useSelectActiveEvent();
  const purchaseSeatTab = router?.query?.purchaseSeatTab;
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedPack = useAppStore(selectSelectedPack);

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [selectedEvent, setSelectedEvent] = useState<EventType>();

  function onRemoveEvent(evt: EventType) {
    dispatch(eventsState.actions.removeEvent(evt.eventCode));
  }

  const visibleEvents = selectedEvents.slice(0, props.maxVisibleIconCount);
  const hiddenEvents = selectedEvents.slice(props.maxVisibleIconCount);

  const selectedEventsQuantity = selectedEvents.length;
  const bottomBarEventsDescription = languages?.purchaseSeats
    ?.bottomBarEventsDescription
    ? pluralize(
        languages?.purchaseSeats?.bottomBarEventsDescription,
        selectedEventsQuantity
      )
    : pluralize("Game", selectedEventsQuantity);

  return (
    <Box width={"100%"}>
      {selectedEvents.length > 0 && (
        <>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={"500"}
              noWrap
              lineHeight={1.1}
            >
              {selectedEvents.length} {bottomBarEventsDescription}
            </Typography>
          </Box>

          <Stack
            direction={"row"}
            mt={1.5}
            sx={styles.eventIconWraper}
            flexWrap={"wrap"}
            rowGap={1}
            columnGap={1}
          >
            {visibleEvents.map((itm, idx) => {
              const isPackBonus = isPackBonusEvent(itm, selectedPack);

              const logoUrl = (
                purchaseSeatConfigs?.useBackgroundUrlForEventLogo
                  ? itm.backgroundUrl
                  : itm.logoUrl
              ) as string;

              if (!logoUrl) return null;

              return (
                <EventIcon
                  key={idx}
                  logoUrl={logoUrl}
                  isSelected={
                    (!!selectedEvent &&
                      selectedEvent.eventCode == itm.eventCode) ||
                    (!!activeEvent && activeEvent.eventCode == itm.eventCode)
                  }
                  onClick={(ev) => {
                    setAnchorEl(ev?.currentTarget);
                    setSelectedEvent(itm);
                  }}
                  isCloseIconVisible={
                    purchaseSeatTab == purchaseSeatsTabUIDs.findGames &&
                    (itm?.minEventSelectionRequired > 0 || isPackBonus)
                  }
                  onClickCloseIcon={(ev) => onRemoveEvent(itm)}
                />
              );
            })}

            {hiddenEvents.length > 0 && (
              <HiddenEventIcon
                hiddenEvents={hiddenEvents}
                selectedEvents={selectedEvents}
                activeEvent={activeEvent}
                onRemoveEvent={onRemoveEvent}
              >
                <Typography
                  fontSize={"14px"}
                  lineHeight={1.1}
                  color={theme.palette.text.secondary}
                  fontWeight={"500"}
                >
                  +{hiddenEvents.length}
                </Typography>
              </HiddenEventIcon>
            )}

            {selectedEvent ? (
              <Popover
                open={isPopoverOpen}
                anchorEl={anchorEl}
                onClose={() => {
                  setAnchorEl(null);
                  setSelectedEvent(undefined);
                }}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      border: `1px solid ${theme.palette.divider}`,
                    },
                  },
                }}
              >
                <Stack direction="column" gap={0.5} p={2}>
                  <Stack>
                    <Typography fontSize={"1rem"} fontWeight={700}>
                      {selectedEvent.title}
                    </Typography>
                  </Stack>

                  <Stack
                    direction={"row"}
                    gap="0.25rem"
                    alignItems={"center"}
                    flexGrow={1}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontSize={"0.9rem"}
                      fontWeight={"500"}
                    >
                      {convertDate(selectedEvent.date)}
                    </Typography>

                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontSize={"0.9rem"}
                      fontWeight={"500"}
                    >
                      |
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontSize={"0.9rem"}
                      fontWeight={"500"}
                    >
                      {convertTime(selectedEvent.time)}
                    </Typography>
                  </Stack>
                </Stack>
              </Popover>
            ) : null}
          </Stack>
        </>
      )}
    </Box>
  );
}

function HiddenEventIcon(props: {
  children: React.ReactNode;
  hiddenEvents: EventType[];
  selectedEvents: EventType[];
  activeEvent: EventType | null;
  onRemoveEvent: (evt: EventType) => void;
}) {
  const { purchaseSeatConfigs } = useTeamConfig();
  const router = useRouter();
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const purchaseSeatTab = router?.query?.purchaseSeatTab;
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [selectedEvent, setSelectedEvent] = useState<EventType>();

  return (
    <>
      <Box
        onClick={(ev) => {
          setAnchorEl(ev?.currentTarget);
        }}
        sx={{
          outline: `1px solid ${theme.palette.divider}`,
          width: mdDown ? "28px" : "50px",
          height: mdDown ? "28px" : "50px",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "8px",
          backgroundColor: colors.grey[100],
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        <ButtonBase sx={{ width: "100%", height: "100%" }}>
          {props?.children}
        </ButtonBase>
      </Box>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSelectedEvent(undefined);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${theme.palette.divider}`,
            },
          },
        }}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          pl={2}
          pr={1}
          py={1}
        >
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={"600"}
              color={theme.palette.text.primary}
              lineHeight={1.1}
            >
              {props.hiddenEvents.length} of {props.selectedEvents.length}{" "}
              {props.hiddenEvents.length > 1 ? "Games" : "Game"}
            </Typography>
          </Box>
          <Box width={"32px"} />
          <Box>
            <IconButton
              onClick={() => {
                setAnchorEl(null);
                setSelectedEvent(undefined);
              }}
              size="small"
              sx={{
                bgcolor: `${colors.grey[100]} !important`,
              }}
            >
              <CloseRounded />
            </IconButton>
          </Box>
        </Stack>
        <Divider />
        <Box p={2} position={"relative"}>
          <Stack direction={"row"} flexWrap={"wrap"} rowGap={1} columnGap={1}>
            {props.hiddenEvents.map((itm, idx) => {
              const logoUrl = (
                purchaseSeatConfigs?.useBackgroundUrlForEventLogo
                  ? itm.backgroundUrl
                  : itm.logoUrl
              ) as string;

              return (
                <EventIcon
                  key={idx}
                  logoUrl={logoUrl}
                  isSelected={
                    (!!selectedEvent &&
                      selectedEvent.eventCode == itm.eventCode) ||
                    (!!props?.activeEvent &&
                      props?.activeEvent?.eventCode == itm.eventCode)
                  }
                  onClick={() => setSelectedEvent(itm)}
                  isCloseIconVisible={
                    purchaseSeatTab == purchaseSeatsTabUIDs.findGames &&
                    itm?.minEventSelectionRequired > 0
                  }
                  onClickCloseIcon={() => props.onRemoveEvent(itm)}
                />
              );
            })}
          </Stack>

          {selectedEvent ? (
            <Stack direction="column" gap={0.5} mt={2}>
              <Stack>
                <Typography fontSize={"1rem"} fontWeight={700}>
                  {selectedEvent.title}
                </Typography>
              </Stack>

              <Stack
                direction={"row"}
                gap="0.25rem"
                alignItems={"center"}
                flexGrow={1}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={"0.9rem"}
                  fontWeight={"500"}
                >
                  {convertDate(selectedEvent.date)}
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={"0.9rem"}
                  fontWeight={"500"}
                >
                  |
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={"0.9rem"}
                  fontWeight={"500"}
                >
                  {convertTime(selectedEvent.time)}
                </Typography>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}

function EventIcon({
  logoUrl,
  isSelected,
  isLoading,
  onClick,
  isCloseIconVisible,
  onClickCloseIcon,
}: {
  logoUrl: string;
  isSelected: boolean;
  onClick?: (e: any) => void;
  isLoading?: boolean;
  isCloseIconVisible?: boolean;
  onClickCloseIcon?: (e: any) => void;
}) {
  const { md: mdDown } = useCssMediaQuery("down");
  const theme = useTheme();
  const styles = useEventIconStyles(theme, { isSelected });

  return (
    <Box
      sx={styles.eventIcon}
      onClick={(e) => {
        e?.stopPropagation();
        onClick?.(e);
      }}
      width={mdDown ? "28px" : "42px"}
      height={mdDown ? "28px" : "42px"}
    >
      {/* close icon for special events */}
      {isCloseIconVisible ? (
        <Box
          onClick={(e) => {
            e?.stopPropagation();
            onClickCloseIcon?.(e);
          }}
          sx={{
            position: "absolute",
            top: 0,
            right: "8px",
            transform: "translate(50%, -50%)",
            width: "24px",
            height: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: colors.grey[100],
            cursor: "pointer",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "50%",
            transition: "all 0.3s ease",
            overflow: "hidden",
            "&:hover": {
              cursor: "pointer",
              backgroundColor: colors.grey[300],
            },
          }}
        >
          <ButtonBase sx={{ width: "100%", height: "100%" }}>
            <CloseRounded style={{ fontSize: "18px" }} />
          </ButtonBase>
        </Box>
      ) : null}

      {isLoading ? (
        <Box sx={styles.eventIconLoadingIndicator}>
          <CircularProgress size={"24px"} />
        </Box>
      ) : null}

      <NextImage
        src={logoUrl}
        alt={"event-icon"}
        width={mdDown ? 28 : 42}
        height={mdDown ? 28 : 42}
        style={{
          objectFit: "contain",
          cursor: "pointer",
        }}
      />
    </Box>
  );
}

function useEventIconWrapperStyles(theme: Theme) {
  return {
    eventIconWraper: {
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
  };
}

function useEventIconStyles(theme: Theme, states: any) {
  let rgbGray = hexToRgb(theme.palette.grey[300]);
  rgbGray = rgbGray.replace("rgb", "").replace("(", "").replace(")", "");

  return {
    eventIcon: {
      position: "relative",
      outline: `${states.isSelected ? "2px" : "1px"}  solid ${
        states.isSelected ? theme.palette.primary.main : theme.palette.divider
      }`,
      borderRadius: "8px",
    },

    eventIconRipple: {
      position: "absolute",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
    },

    eventIconLoadingIndicator: {
      position: "absolute",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: `rgba(${rgbGray}, 0.4)`,
    },
  };
}
